import type { DisplayPreferencePage, DisplayPreferenceScope } from '@/stores/auth'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'

/** Shared debounce window before a display-preference edit is persisted to the server. */
export const DISPLAY_PREFERENCE_DEBOUNCE_MS = 600

/** Scope key for a project: org + tenant, matching the convention used across all display preferences. */
export function tenantDisplayPreferenceScope(orgId: string, tenantId: string): string {
	return `${orgId}:${tenantId}`
}

/** Scope key for an org-wide (non-tenant) view, e.g. org-level device inventory. */
export function orgDisplayPreferenceScope(orgId: string): string {
	return `org:${orgId}`
}

/**
 * A persisted `columns` array mixes namespaces, each entry prefixed (e.g. "metadata::speed"). These
 * two read and write one namespace's slice of it while leaving the other namespaces' entries, and
 * the overall column order, untouched.
 */
export function selectedIdsForPrefix(columns: string[], prefix: string): string[] {
	return columns.filter(c => c.startsWith(prefix)).map(c => c.slice(prefix.length))
}

/** Keeps the positions of ids that stay selected, drops the deselected, appends the new at the end. */
export function applySelectionForPrefix(columns: string[], prefix: string, nextIds: string[]): string[] {
	const nextSet = new Set(nextIds)
	const kept = columns.filter(c => (c.startsWith(prefix) ? nextSet.has(c.slice(prefix.length)) : true))
	const keptIds = new Set(selectedIdsForPrefix(kept, prefix))
	const appended = nextIds.filter(id => !keptIds.has(id)).map(id => `${prefix}${id}`)
	return [...kept, ...appended]
}

/**
 * The same merge for a page whose optional columns are plain ids rather than a namespace:
 * `optionalIds` are the entries the picker owns, so ordering entries for always-on columns stay put.
 */
export function applySelectionForIds(columns: string[], optionalIds: readonly string[], nextIds: string[]): string[] {
	const nextSet = new Set(nextIds)
	const kept = columns.filter(c => !optionalIds.includes(c) || nextSet.has(c))
	const keptSet = new Set(kept)
	return [...kept, ...nextIds.filter(id => !keptSet.has(id))]
}

/** Point-in-time read of one page's display-preferences bag for a given scope. */
export function readDisplayPreferencePage(scopeKey: string | null, pageKey: string): DisplayPreferencePage | undefined {
	if (!scopeKey) {
		return undefined
	}
	const user = useAuthStore.getState().user
	if (!user || !isPlatformUser(user)) {
		return undefined
	}
	return user.settings?.displayPreferences?.[scopeKey]?.[pageKey]
}

/**
 * Tab reordering is gone, so the `tabOrder` its pages persisted is dead weight in every user's
 * settings. Nothing writes the key any more, so stripping it on the next write of any preference
 * clears it without a migration. Returns the same object when there is nothing to drop, so an
 * ordinary write does not churn the tree. Remove this once the stored keys have aged out.
 */
function withoutRetiredKeys(
	preferences: Record<string, DisplayPreferenceScope>,
): Record<string, DisplayPreferenceScope> {
	let changed = false
	const pruned: Record<string, DisplayPreferenceScope> = {}

	for (const [scope, pages] of Object.entries(preferences)) {
		const prunedPages: DisplayPreferenceScope = {}
		for (const [page, bag] of Object.entries(pages)) {
			if (bag && typeof bag === 'object' && 'tabOrder' in bag) {
				const { tabOrder: _tabOrder, ...rest } = bag
				prunedPages[page] = rest
				changed = true
			} else {
				prunedPages[page] = bag
			}
		}
		pruned[scope] = prunedPages
	}

	return changed ? pruned : preferences
}

/**
 * Merges `patch` into one page's display-preferences bag and persists it via the auth store.
 * Reads current settings inside the queued updater (not before), so it never overwrites another
 * write still in flight.
 */
export function writeDisplayPreferencePage(
	scopeKey: string | null,
	pageKey: string,
	patch: Record<string, unknown>,
): void {
	if (!scopeKey) {
		return
	}
	useAuthStore
		.getState()
		.updateUserSettingsWith(current => {
			const existingPreferences = withoutRetiredKeys(current.displayPreferences ?? {})
			const existingScope = existingPreferences[scopeKey] ?? {}
			const existingPage = existingScope[pageKey] ?? {}
			return {
				...current,
				displayPreferences: {
					...existingPreferences,
					[scopeKey]: { ...existingScope, [pageKey]: { ...existingPage, ...patch } },
				},
			}
		})
		.catch(error => {
			console.warn(`Failed to persist display preference ${pageKey}:`, error)
		})
}
